"use client"

import { useRouter } from "next/navigation"
import type { ILesson } from "@/lib/types/course/lesson"
import type { CourseDetail } from "@/lib/types/course/detail"
import { Price } from "./price"
import { Heart } from "lucide-react"
import { Button } from "@/components/ui/button"
import { useCourseActions } from "@/lib/hooks/useCourseActions"
import { CountdownTimer } from "@/components/ui/CountdownTimer"
import { formatCurrency } from "@/lib/utils"
import { COURSE_STATUS } from "@/lib/constants/course"
import { Formatter } from "@/lib/helpers/formatter"

interface CourseActionsProps {
	course: CourseDetail
	className?: string
	showTimer?: boolean
}

const CourseActions: React.FC<CourseActionsProps> = ({
	course,
	showTimer = true,
	className = "md:px-6 md:py-4 px-3 py-2",
}) => {

	const { isLoading, isWhitelisted, handleWhitelist, handleBuy } = useCourseActions({
		course,
		initialIsWhitelisted: course.is_whitelist,
	})


	const getButtonText = Price.getButtonText(
		course.status.status,
		course.status.all_status,
	)
	
	let priceContent = (
		<div className="mb-2 space-y-3">
			<div className="flex flex-wrap items-center gap-2">
				<span className="font-bold text-2xl text-primary">
					{Formatter.formatMoney(course.price_discount)}
				</span>
				{course.price_discount < course.price && (
					<>
						<span className="text-gray-500 text-sm line-through">
							{Formatter.formatMoney(course.price)}
						</span>
						{course.discount > 0 && (
							<span className="rounded-full bg-red-100 px-2 py-1 font-medium text-red-700 text-xs">
								-{course.discount}% OFF
							</span>
						)}
					</>
				)}
			</div>
			{showTimer && course.promotion_deadline && course.discount > 0 && (
				<CountdownTimer
					deadline={course.promotion_deadline}
					className="w-full"
				/>
			)}
		</div>
	)
	if (course.status.status === COURSE_STATUS.CONTINUE_LEARNING || course.status.status === COURSE_STATUS.GIFTED) {
		priceContent = <></>
	}

	return (
		<div className={className}>
			{priceContent}
			<div className="mb-2 flex gap-2">
				<button
					type="button"
					className={`${course.status.status === 0 || course.status.status === 2 ? "bg-teal-500 hover:bg-teal-700" : "bg-blue-500 hover:bg-blue-700"} flex-1 rounded px-4 py-2 font-bold text-white`}
					onClick={handleBuy}
					disabled={isLoading}
				>
					{getButtonText}
				</button>
				<Button
					variant="ghost"
					size="icon"
					className="h-10 w-10 text-destructive hover:bg-destructive/10 hover:text-destructive"
					onClick={handleWhitelist}
					disabled={isLoading}
				>
					<Heart className={`h-5 w-5 ${isWhitelisted ? "fill-current" : ""}`} />
				</Button>
			</div>
		</div>
	)
}

export default CourseActions
